/**
 * useEditHistory.js (iter-39) — thin client hook that talks to
 * ``/api/books/:id/history`` so any editor surface (BookEditor,
 * StructurePanel, ManualCompose) can capture a "before" snapshot,
 * list the timeline, and revert with one click.
 *
 * Usage:
 *   const history = useEditHistory(bookId, setBook);
 *   history.record("chapter_rename", `Renamed "${old}" → "${next}"`);  // fire-and-forget
 *   history.revert(entryId);                                             // restores + updates book
 *   history.entries; // last 20, newest first
 *
 * The record() call is fire-and-forget so mutation UX stays snappy —
 * we don't await it before firing the actual PATCH. The backend
 * snapshots the DB row at record-time so the ordering doesn't matter.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

export function useEditHistory(bookId, onBookRestored) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(null); // entry id currently being reverted

  const refresh = useCallback(async () => {
    if (!bookId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/books/${bookId}/history`);
      setEntries(data?.entries || []);
    } catch (err) {
      // History is optional UX — swallow to avoid noisy toasts in
      // dashboards that don't have permission or the collection.
      console.debug("history load failed:", err?.message);
    } finally { setLoading(false); }
  }, [bookId]);

  useEffect(() => { refresh(); }, [refresh]);

  const record = useCallback(async (action, description = "") => {
    if (!bookId) return null;
    try {
      const { data } = await api.post(`/books/${bookId}/history/record`,
                                       { action, description });
      // Optimistic prepend so the timeline reflects the new row
      // immediately without a round-trip GET.
      setEntries((prev) => [{
        id: data.id, action: data.action,
        description: data.description, created_at: data.created_at,
      }, ...prev].slice(0, 20));
      return data;
    } catch (err) {
      console.debug("history record failed:", err?.message);
      return null;
    }
  }, [bookId]);

  const revert = useCallback(async (entryId) => {
    if (!bookId || !entryId) return null;
    setReverting(entryId);
    try {
      const { data } = await api.post(`/books/${bookId}/history/${entryId}/revert`);
      onBookRestored?.(data?.book);
      // Refresh so the "Reverted …" pseudo-entry the backend added is
      // visible in the timeline.
      await refresh();
      return data;
    } catch (err) {
      console.debug("history revert failed:", err?.message);
      throw err;
    } finally { setReverting(null); }
  }, [bookId, onBookRestored, refresh]);

  return { entries, loading, reverting, record, revert, refresh };
}
